import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Checkbox,
  Button,
  Paper,
  Stack,
  CircularProgress,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip
} from "@mui/material";
import api from "../../api";
import SendWhatsAppModal from "../../components/SendWhatsAppModal";
import { adminHasPermission } from "../../utils/adminPermissions";
import ResponsiveTable from "../../components/ResponsiveTable";

const AdminLeads = () => {
  const canSendWhatsApp = adminHasPermission("send-whatsapp-msg");
  const [leads, setLeads] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openModal, setOpenModal] = useState(false);

  const [filters, setFilters] = useState({
    name: '',
    email: '',
    course: '',
    source: '',
    status: ''
  });

  useEffect(() => {
    fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLeads = async (customFilters = null) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      const activeFilters = customFilters || filters;

      Object.keys(activeFilters).forEach(key => {
        if (activeFilters[key]) {
          params.append(key, activeFilters[key]);
        }
      });

      const res = await api.get(`/admin/leads?${params.toString()}`);
      setLeads(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching leads", err);
      setError(err.response?.data?.message || 'Failed to fetch leads. Please check your connection or login status.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const handleApplyFilter = () => {
    fetchLeads();
  };

  const handleClearFilter = () => {
    const reset = {
      name: '',
      email: '',
      course: '',
      source: '',
      status: ''
    };
    setFilters(reset);
    fetchLeads(reset);
  };

  const handleSelect = (id) => {
    setSelectedLeads((prev) =>
      prev.includes(id)
        ? prev.filter((item) => item !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedLeads(leads.map((lead) => lead.id));
    } else {
      setSelectedLeads([]);
    }
  };

  const formatSource = (source) => {
    if (source === 'mobile_app_help_support') return 'Mobile Help & Support';
    if (source === 'mobile_app_issue_report') return 'Mobile App Issue';
    if (source === 'website_contact_form') return 'Website Form';
    return source || '-';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'info';
      case 'contacted': return 'warning';
      case 'replied': return 'primary';
      case 'converted': return 'success';
      default: return 'default';
    }
  };

  return (
    <Box p={3}>
      <Typography variant="h5" mb={1}>
        Leads Management
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Mobile app Help &amp; Support messages also appear here (filter Source: Mobile Help &amp; Support). For full
        details and email replies, use{" "}
        <strong>Leads → Mobile Help &amp; Support</strong> in the sidebar.
      </Typography>

      {error && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: '#ffebee', color: '#c62828' }}>
          <Typography>{error}</Typography>
        </Paper>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="Name"
              name="name"
              size="small"
              value={filters.name}
              onChange={handleFilterChange}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              size="small"
              value={filters.email}
              onChange={handleFilterChange}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="Course"
              name="course"
              size="small"
              value={filters.course}
              onChange={handleFilterChange}
              placeholder="Name or ID"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Source</InputLabel>
              <Select
                name="source"
                label="Source"
                value={filters.source}
                onChange={handleFilterChange}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="website_contact_form">Website Form</MenuItem>
                <MenuItem value="mobile_app_help_support">Mobile Help &amp; Support</MenuItem>
                <MenuItem value="mobile_app_issue_report">Mobile App Issue</MenuItem>
                <MenuItem value="manual">Manual</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                label="Status"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="new">New</MenuItem>
                <MenuItem value="contacted">Contacted</MenuItem>
                <MenuItem value="replied">Replied</MenuItem>
                <MenuItem value="converted">Converted</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Stack direction="row" spacing={1}>
              <Button variant="contained" onClick={handleApplyFilter}>Filter</Button>
              <Button variant="outlined" onClick={handleClearFilter}>Clear</Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>


      <Stack direction="row" spacing={2} mb={2} justifyContent="space-between" alignItems="center">
        <Box>
          <Button
            variant="contained"
            disabled={!selectedLeads.length}
            onClick={() => setOpenModal(true)}
            sx={{ mr: 1 }}
          >
            Send WhatsApp ({selectedLeads.length})
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Total Leads: {leads.length}
        </Typography>
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" p={5}>
          <CircularProgress />
        </Box>
      ) : (
        <ResponsiveTable component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {canSendWhatsApp && (
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={leads.length > 0 && selectedLeads.length === leads.length}
                    indeterminate={selectedLeads.length > 0 && selectedLeads.length < leads.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                )}
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Contact No</TableCell>
                <TableCell>Course</TableCell>
                <TableCell>Year & Schools</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {leads.length > 0 ? (
                leads.map((lead) => (
                  <TableRow key={lead.id} hover>
                    {canSendWhatsApp && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedLeads.includes(lead.id)}
                        onChange={() => handleSelect(lead.id)}
                      />
                    </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="subtitle2">{lead.name}</Typography>
                      {lead.notes && (
                        <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ maxWidth: 200 }}>
                          {lead.notes}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{lead.email || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{lead.phone || '-'}</Typography>
                    </TableCell>
                    <TableCell>{lead.course ? lead.course.name : '-'}</TableCell>
                    <TableCell>
                      <Box display="flex" flexDirection="column">
                        <Typography variant="body2">{lead.academic_year?.start_end_year || '-'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {lead.target_schools && lead.target_schools.length > 0
                            ? lead.target_schools.join(', ')
                            : '-'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{formatSource(lead.source)}</TableCell>
                    <TableCell>
                      <Chip
                        label={lead.status}
                        color={getStatusColor(lead.status)}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(lead.created_at).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={canSendWhatsApp ? 8 : 7} align="center">
                    No leads found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ResponsiveTable>
      )}

      {canSendWhatsApp && (
      <SendWhatsAppModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        selectedLeads={selectedLeads}
        refreshLeads={fetchLeads}
      />
      )}
    </Box>
  );
};

export default AdminLeads;
